// PDF Report generator for Contas a Receber / Contas a Pagar
// jsPDF + jspdf-autotable: texto vetorial, paginação correta (sem cortar linhas),
// cabeçalho da tabela repetido em todas as páginas e numeração de página.
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { BRAND } from "@/lib/brand";

const NAVY = [26, 35, 126];
const GOLD = [212, 164, 55];
const TEXT = [33, 37, 41];
const MUTED = [108, 117, 125];
const BORDER = [222, 226, 230];
const ROW_ALT = [245, 246, 250];
const GREEN = [27, 94, 32];
const RED = [183, 28, 28];
const AMBER = [230, 81, 0];

const PAGE_W = 297;
const PAGE_H = 210;
const M = 10;

const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s + "T00:00:00").toLocaleDateString("pt-BR");
  } catch {
    return s;
  }
};

const STATUS_LABELS_MAP = {
  pendente: "Pendente",
  pago: "Pago",
  cancelado: "Cancelado",
  vencido: "Vencido",
};
const STATUS_COLORS = { pendente: AMBER, pago: GREEN, cancelado: MUTED, vencido: RED };

function diasVencido(dataVencimento) {
  if (!dataVencimento) return 0;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const venc = new Date(dataVencimento + "T00:00:00");
  const diff = Math.floor((hoje - venc) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
}

const safeName = (s) => String(s || "").replace(/[^a-zA-Z0-9-_]+/g, "_").slice(0, 30);

// ---------- blocos compartilhados ----------

function drawHeader(doc, { docLabel, tituloParts, subtitle, filtros, dataEmissao }) {
  // barra de marca
  doc.setFillColor(...NAVY);
  doc.rect(M, 8, (PAGE_W - 2 * M) * 0.75, 2.2, "F");
  doc.setFillColor(...GOLD);
  doc.rect(M + (PAGE_W - 2 * M) * 0.75, 8, (PAGE_W - 2 * M) * 0.25, 2.2, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(...NAVY);
  doc.text(BRAND.name, M, 17.5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(BRAND.tagline, M, 21.5);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...NAVY);
  doc.text(docLabel, PAGE_W - M, 16, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(`Emitido em ${dataEmissao}`, PAGE_W - M, 20.5, { align: "right" });

  doc.setDrawColor(...NAVY);
  doc.setLineWidth(0.5);
  doc.line(M, 24, PAGE_W - M, 24);

  // título
  let x = M;
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  for (const part of tituloParts) {
    doc.setTextColor(...(part.color || NAVY));
    doc.text(part.text, x, 31.5);
    x += doc.getTextWidth(part.text) + 1.5;
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(subtitle, M, 36);

  let y = 39;
  if (filtros.length) {
    doc.setFillColor(...ROW_ALT);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.roundedRect(M, y, PAGE_W - 2 * M, 7, 1, 1, "FD");
    doc.setFontSize(7.5);
    doc.setTextColor(...TEXT);
    let fx = M + 3;
    for (const f of filtros) {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...NAVY);
      doc.text(f.label + ":", fx, y + 4.5);
      fx += doc.getTextWidth(f.label + ":") + 1.2;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...TEXT);
      doc.text(f.value, fx, y + 4.5);
      fx += doc.getTextWidth(f.value) + 6;
    }
    y += 10;
  }
  return y;
}

function drawCards(doc, y, cards) {
  const gap = 4;
  const w = (PAGE_W - 2 * M - gap * (cards.length - 1)) / cards.length;
  cards.forEach((c, i) => {
    const x = M + i * (w + gap);
    doc.setFillColor(...(c.bg || [255, 255, 255]));
    doc.setDrawColor(...(c.border || BORDER));
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, w, 13, 1.2, 1.2, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.setTextColor(...MUTED);
    doc.text(c.label.toUpperCase(), x + 3, y + 4.5);
    doc.setFontSize(11);
    doc.setTextColor(...(c.color || NAVY));
    doc.text(c.value, x + 3, y + 10);
  });
  return y + 17;
}

function drawAlerta(doc, y, texto) {
  doc.setFillColor(255, 243, 224);
  doc.setDrawColor(...AMBER);
  doc.setLineWidth(0.2);
  doc.roundedRect(M, y, PAGE_W - 2 * M, 7.5, 1, 1, "FD");
  doc.setFillColor(...AMBER);
  doc.rect(M, y, 1.4, 7.5, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(...AMBER);
  doc.text(texto, M + 4, y + 4.8);
  return y + 11;
}

function drawFooters(doc, registrosInfo, dataEmissao) {
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.line(M, PAGE_H - 10, PAGE_W - M, PAGE_H - 10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...NAVY);
    doc.text(BRAND.fullName, M, PAGE_H - 6.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...MUTED);
    doc.text("Documento de uso interno", M, PAGE_H - 3.5);
    doc.text(`${registrosInfo} • Gerado em ${dataEmissao}`, PAGE_W - M, PAGE_H - 6.5, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...NAVY);
    doc.text(`Página ${i} de ${total}`, PAGE_W - M, PAGE_H - 3.5, { align: "right" });
  }
}

const TABLE_STYLES = {
  theme: "plain",
  styles: {
    font: "helvetica",
    fontSize: 7,
    cellPadding: { top: 1.5, bottom: 1.5, left: 1.5, right: 1.5 },
    textColor: TEXT,
    lineColor: BORDER,
    lineWidth: { bottom: 0.15 },
    overflow: "linebreak",
    valign: "middle",
  },
  headStyles: {
    fillColor: NAVY,
    textColor: [255, 255, 255],
    fontStyle: "bold",
    fontSize: 7,
    lineWidth: 0,
    cellPadding: { top: 2.2, bottom: 2.2, left: 1.5, right: 1.5 },
  },
  alternateRowStyles: { fillColor: ROW_ALT },
  footStyles: {
    fillColor: [233, 236, 243],
    textColor: NAVY,
    fontStyle: "bold",
    fontSize: 7.2,
    lineWidth: 0,
    cellPadding: { top: 2.2, bottom: 2.2, left: 1.5, right: 1.5 },
  },
  rowPageBreak: "avoid",
  showHead: "everyPage",
  showFoot: "lastPage",
  margin: { top: 12, left: M, right: M, bottom: 14 },
};

// Kit compartilhado para outros relatórios seguirem o mesmo padrão visual
export const pdfKit = {
  NAVY, GOLD, TEXT, MUTED, BORDER, ROW_ALT, GREEN, RED, AMBER,
  PAGE_W, PAGE_H, M,
  fmtBRL, fmtDate,
  drawHeader, drawCards, drawAlerta, drawFooters,
  TABLE_STYLES,
};

// ---------- Relatório individual (Receber OU Pagar) ----------

export async function gerarRelatorioFinanceiroPDF({ tipo, items, filters, totais }) {
  const isReceber = tipo === "receber";
  const titulo = isReceber ? "Contas a Receber" : "Contas a Pagar";
  const colEntidade = isReceber ? "Cliente" : "Fornecedor";
  const dataEmissao = new Date().toLocaleString("pt-BR");
  const hoje = new Date().toISOString().split("T")[0];

  const sorted = [...items].sort((a, b) =>
    (a.data_vencimento || "9999-12-31").localeCompare(b.data_vencimento || "9999-12-31"));

  const totalVencidos = sorted.filter(
    (i) => (i.status_display || i.status) === "vencido" || (i.status === "pendente" && i.data_vencimento < hoje));
  const totalAtrasoDias = totalVencidos.reduce((acc, i) => acc + diasVencido(i.data_vencimento), 0);
  const mediaAtraso = totalVencidos.length > 0 ? Math.round(totalAtrasoDias / totalVencidos.length) : 0;

  const filtros = [];
  if (filters.dataInicio || filters.dataFim)
    filtros.push({ label: "Período", value: `${fmtDate(filters.dataInicio)} a ${fmtDate(filters.dataFim)}` });
  if (filters.status && filters.status !== "all")
    filtros.push({ label: "Status", value: STATUS_LABELS_MAP[filters.status] || filters.status });
  if (filters.entidadeNome) filtros.push({ label: colEntidade, value: String(filters.entidadeNome) });
  if (filters.search) filtros.push({ label: "Busca", value: `"${filters.search}"` });

  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape", compress: true });

  let y = drawHeader(doc, {
    docLabel: "Relatório Financeiro",
    tituloParts: [
      { text: "Relatório de", color: NAVY },
      { text: titulo, color: isReceber ? GREEN : AMBER },
    ],
    subtitle: "Listagem detalhada por vencimento, com totais agregados e indicação de atrasos.",
    filtros,
    dataEmissao,
  });

  y = drawCards(doc, y, [
    { label: "Total no Período", value: fmtBRL(totais.total) },
    { label: "Pendente", value: fmtBRL(totais.pendente), bg: [255, 243, 224], border: [255, 204, 128], color: AMBER },
    { label: isReceber ? "Recebido" : "Pago", value: fmtBRL(totais.pago), bg: [232, 245, 233], border: [165, 214, 167], color: GREEN },
    { label: "Vencido", value: fmtBRL(totais.vencido), bg: [255, 235, 238], border: [239, 154, 154], color: RED },
  ]);

  if (totalVencidos.length > 0) {
    y = drawAlerta(doc, y,
      `Atenção: ${totalVencidos.length} ${totalVencidos.length === 1 ? "lançamento vencido" : "lançamentos vencidos"} totalizando ${fmtBRL(totais.vencido)}. Atraso médio de ${mediaAtraso} ${mediaAtraso === 1 ? "dia" : "dias"}.`);
  }

  if (sorted.length === 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text("Nenhum lançamento encontrado para os filtros aplicados.", PAGE_W / 2, y + 15, { align: "center" });
  } else {
    const body = sorted.map((item) => {
      const statusEff = item.status_display || item.status;
      const dias = statusEff === "vencido" ? diasVencido(item.data_vencimento) : 0;
      const entNome = isReceber ? item.cliente_nome : item.fornecedor_nome || item.cliente_nome;
      const docNum = isReceber
        ? (item.proposta_numero ? `#${item.proposta_numero}` : "—")
        : (item.documento || "—");
      return [
        fmtDate(item.data_vencimento),
        entNome || "—",
        docNum,
        item.parcela || "—",
        item.descricao || "—",
        fmtBRL(item.valor),
        Number(item.juros || 0) > 0 ? fmtBRL(item.juros) : "—",
        Number(item.multa || 0) > 0 ? fmtBRL(item.multa) : "—",
        STATUS_LABELS_MAP[statusEff] || statusEff,
        item.data_pagamento ? fmtDate(item.data_pagamento) : "—",
        item.banco_nome || "—",
        dias > 0 ? `${dias}d` : "—",
      ];
    });
    const somaValor = sorted.reduce((a, i) => a + Number(i.valor || 0), 0);
    const somaJuros = sorted.reduce((a, i) => a + Number(i.juros || 0), 0);
    const somaMulta = sorted.reduce((a, i) => a + Number(i.multa || 0), 0);

    autoTable(doc, {
      ...TABLE_STYLES,
      startY: y,
      head: [[
        "Vencimento", colEntidade, isReceber ? "Nº Proposta" : "Documento", "Parcela", "Descrição",
        "Valor", "Juros", "Multa", "Status", isReceber ? "Recebimento" : "Pagamento", "Banco", "Atraso",
      ]],
      body,
      foot: [[
        "TOTAL", "", "", "", `${sorted.length} ${sorted.length === 1 ? "registro" : "registros"}`,
        fmtBRL(somaValor), somaJuros > 0 ? fmtBRL(somaJuros) : "—", somaMulta > 0 ? fmtBRL(somaMulta) : "—",
        "", "", "", "",
      ]],
      columnStyles: {
        0: { cellWidth: 18, halign: "center" },
        1: { cellWidth: 42 },
        2: { cellWidth: 18, halign: "center" },
        3: { cellWidth: 13, halign: "center" },
        4: { cellWidth: 55 },
        5: { cellWidth: 22, halign: "right", fontStyle: "bold" },
        6: { cellWidth: 16, halign: "right" },
        7: { cellWidth: 16, halign: "right" },
        8: { cellWidth: 16, halign: "center", fontStyle: "bold" },
        9: { cellWidth: 19, halign: "center" },
        10: { cellWidth: 29 },
        11: { cellWidth: 13, halign: "center" },
      },
      didParseCell: (data) => {
        if (data.section !== "body") return;
        if (data.column.index === 8) {
          const st = Object.keys(STATUS_LABELS_MAP).find((k) => STATUS_LABELS_MAP[k] === data.cell.raw);
          if (st && STATUS_COLORS[st]) data.cell.styles.textColor = STATUS_COLORS[st];
        }
        if (data.column.index === 11 && data.cell.raw !== "—") {
          data.cell.styles.textColor = RED;
          data.cell.styles.fontStyle = "bold";
        }
      },
    });
  }

  drawFooters(doc, `${sorted.length} ${sorted.length === 1 ? "registro" : "registros"}`, dataEmissao);

  const filename = `Relatorio_${isReceber ? "Contas-a-Receber" : "Contas-a-Pagar"}_${safeName(filters.dataInicio)}_a_${safeName(filters.dataFim)}.pdf`;
  return { blob: doc.output("blob"), filename };
}

// ---------- Relatório agrupado por Cliente/Fornecedor ----------

export async function gerarRelatorioPorClientePDF({ tipo, items, filters, totais }) {
  const isReceber = tipo === "receber";
  const entidadeLabel = isReceber ? "Cliente" : "Fornecedor";
  const dataEmissao = new Date().toLocaleString("pt-BR");
  const GROUP_BG = [228, 232, 242];
  const SUBTOTAL_BG = [245, 246, 250];

  const nomeEnt = (i) => (isReceber ? i.cliente_nome : i.fornecedor_nome || i.cliente_nome) || `(Sem ${entidadeLabel.toLowerCase()})`;
  const grupos = {};
  for (const i of items) (grupos[nomeEnt(i)] = grupos[nomeEnt(i)] || []).push(i);
  const nomes = Object.keys(grupos).sort((a, b) => {
    const va = grupos[a].reduce((s, i) => s + Number(i.valor || 0), 0);
    const vb = grupos[b].reduce((s, i) => s + Number(i.valor || 0), 0);
    return vb - va;
  });

  const filtros = [];
  if (filters.dataInicio || filters.dataFim)
    filtros.push({ label: "Período", value: `${fmtDate(filters.dataInicio)} a ${fmtDate(filters.dataFim)}` });
  if (filters.status && filters.status !== "all")
    filtros.push({ label: "Status", value: STATUS_LABELS_MAP[filters.status] || filters.status });
  filtros.push({ label: "Agrupamento", value: `Por ${entidadeLabel}` });

  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape", compress: true });
  let y = drawHeader(doc, {
    docLabel: "Relatório Financeiro",
    tituloParts: [
      { text: isReceber ? "Contas a Receber" : "Contas a Pagar", color: isReceber ? GREEN : AMBER },
      { text: `por ${entidadeLabel}`, color: NAVY },
    ],
    subtitle: `Lançamentos agrupados por ${entidadeLabel.toLowerCase()} com subtotais — ideal para cobrança e conferência.`,
    filtros,
    dataEmissao,
  });

  y = drawCards(doc, y, [
    { label: "Total no Período", value: fmtBRL(totais.total) },
    { label: `${entidadeLabel}s`, value: String(nomes.length) },
    { label: "Em Aberto (pend.+venc.)", value: fmtBRL((totais.pendente || 0) + (totais.vencido || 0)), bg: [255, 243, 224], border: [255, 204, 128], color: AMBER },
    { label: isReceber ? "Recebido" : "Pago", value: fmtBRL(totais.pago), bg: [232, 245, 233], border: [165, 214, 167], color: GREEN },
  ]);

  const head = ["Vencimento", isReceber ? "Nº Proposta" : "Documento", "Parcela", "Descrição", "Valor", "Juros", "Multa", "Status", isReceber ? "Recebimento" : "Pagamento", "Banco", "Atraso"];
  const nCols = head.length;
  const body = [];
  for (const nome of nomes) {
    const lista = grupos[nome].sort((a, b) =>
      (a.data_vencimento || "9999").localeCompare(b.data_vencimento || "9999"));
    const totalEnt = lista.reduce((s, i) => s + Number(i.valor || 0), 0);
    const abertoEnt = lista.reduce((s, i) => {
      const st = i.status_display || i.status;
      return s + (st === "pendente" || st === "vencido" ? Number(i.valor || 0) : 0);
    }, 0);
    body.push([{
      content: `${nome}   •   ${lista.length} ${lista.length === 1 ? "lançamento" : "lançamentos"}`,
      colSpan: nCols,
      styles: { fillColor: GROUP_BG, textColor: NAVY, fontStyle: "bold", fontSize: 7.5, cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 } },
    }]);
    for (const item of lista) {
      const statusEff = item.status_display || item.status;
      const dias = statusEff === "vencido" ? diasVencido(item.data_vencimento) : 0;
      body.push([
        fmtDate(item.data_vencimento),
        isReceber ? (item.proposta_numero ? `#${item.proposta_numero}` : "—") : (item.documento || "—"),
        item.parcela || "—",
        item.descricao || "—",
        fmtBRL(item.valor),
        Number(item.juros || 0) > 0 ? fmtBRL(item.juros) : "—",
        Number(item.multa || 0) > 0 ? fmtBRL(item.multa) : "—",
        STATUS_LABELS_MAP[statusEff] || statusEff,
        item.data_pagamento ? fmtDate(item.data_pagamento) : "—",
        item.banco_nome || "—",
        dias > 0 ? `${dias}d` : "—",
      ]);
    }
    body.push([
      {
        content: `Subtotal ${nome}${abertoEnt > 0.004 ? `   (em aberto: ${fmtBRL(abertoEnt)})` : ""}`,
        colSpan: 4,
        styles: { fillColor: SUBTOTAL_BG, textColor: TEXT, fontStyle: "bold", halign: "right" },
      },
      { content: fmtBRL(totalEnt), styles: { fillColor: SUBTOTAL_BG, textColor: NAVY, fontStyle: "bold", halign: "right" } },
      ...Array(nCols - 5).fill({ content: "", styles: { fillColor: SUBTOTAL_BG } }),
    ]);
  }

  const somaValor = items.reduce((s, i) => s + Number(i.valor || 0), 0);
  autoTable(doc, {
    ...TABLE_STYLES,
    startY: y,
    head: [head],
    body,
    foot: [[
      { content: "TOTAL GERAL", colSpan: 4, styles: { halign: "right" } },
      { content: fmtBRL(somaValor), styles: { halign: "right" } },
      ...Array(nCols - 5).fill(""),
    ]],
    columnStyles: {
      0: { cellWidth: 19, halign: "center" },
      1: { cellWidth: 19, halign: "center" },
      2: { cellWidth: 13, halign: "center" },
      3: { cellWidth: 92 },
      4: { cellWidth: 23, halign: "right", fontStyle: "bold" },
      5: { cellWidth: 16, halign: "right" },
      6: { cellWidth: 16, halign: "right" },
      7: { cellWidth: 17, halign: "center", fontStyle: "bold" },
      8: { cellWidth: 19, halign: "center" },
      9: { cellWidth: 29 },
      10: { cellWidth: 14, halign: "center" },
    },
    alternateRowStyles: {},
    didParseCell: (data) => {
      if (data.section !== "body") return;
      if (data.column.index === 7) {
        const st = Object.keys(STATUS_LABELS_MAP).find((k) => STATUS_LABELS_MAP[k] === data.cell.raw);
        if (st && STATUS_COLORS[st]) data.cell.styles.textColor = STATUS_COLORS[st];
      }
      if (data.column.index === 10 && data.cell.raw !== "—" && typeof data.cell.raw === "string") {
        data.cell.styles.textColor = RED;
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  drawFooters(doc, `${items.length} ${items.length === 1 ? "registro" : "registros"} • ${nomes.length} ${entidadeLabel.toLowerCase()}(s)`, dataEmissao);
  const filename = `Relatorio_${isReceber ? "Receber" : "Pagar"}_por_${entidadeLabel}_${safeName(filters.dataInicio)}_a_${safeName(filters.dataFim)}.pdf`;
  return { blob: doc.output("blob"), filename };
}

// ---------- Relatório consolidado (Pagar + Receber) ----------

export async function gerarRelatorioConsolidadoPDF({ items, filters }) {
  const dataEmissao = new Date().toLocaleString("pt-BR");
  const hoje = new Date().toISOString().split("T")[0];

  const withStatus = items.map((i) => ({
    ...i,
    status_display:
      i.status === "pendente" && i.data_vencimento && i.data_vencimento < hoje ? "vencido" : i.status,
  }));
  const sorted = [...withStatus].sort((a, b) =>
    (a.data_vencimento || "9999-12-31").localeCompare(b.data_vencimento || "9999-12-31"));

  const receitas = sorted.filter((i) => i.tipo === "receita");
  const despesas = sorted.filter((i) => i.tipo === "despesa");
  const totalReceber = receitas.reduce((acc, i) => acc + (i.valor || 0), 0);
  const totalPagar = despesas.reduce((acc, i) => acc + (i.valor || 0), 0);
  const saldoPrevisto = totalReceber - totalPagar;
  const totalVencido = sorted
    .filter((i) => i.status_display === "vencido")
    .reduce((acc, i) => acc + (i.valor || 0), 0);

  const filtros = [];
  if (filters.dataInicio || filters.dataFim)
    filtros.push({ label: "Período", value: `${fmtDate(filters.dataInicio)} a ${fmtDate(filters.dataFim)}` });
  if (filters.status && filters.status !== "all")
    filtros.push({ label: "Status", value: STATUS_LABELS_MAP[filters.status] || filters.status });

  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape", compress: true });

  let y = drawHeader(doc, {
    docLabel: "Relatório Financeiro Consolidado",
    tituloParts: [
      { text: "Contas a", color: NAVY },
      { text: "Receber", color: GREEN },
      { text: "e a", color: NAVY },
      { text: "Pagar", color: RED },
    ],
    subtitle: "Relatório consolidado ordenado por vencimento, com data de pagamento, banco e saldo previsto.",
    filtros,
    dataEmissao,
  });

  y = drawCards(doc, y, [
    { label: "Total a Receber", value: fmtBRL(totalReceber), bg: [232, 245, 233], border: [165, 214, 167], color: GREEN },
    { label: "Total a Pagar", value: fmtBRL(totalPagar), bg: [255, 235, 238], border: [239, 154, 154], color: RED },
    {
      label: "Saldo Previsto",
      value: `${saldoPrevisto >= 0 ? "" : "-"}${fmtBRL(Math.abs(saldoPrevisto))}`,
      bg: saldoPrevisto >= 0 ? [232, 245, 233] : [255, 235, 238],
      border: saldoPrevisto >= 0 ? [165, 214, 167] : [239, 154, 154],
      color: saldoPrevisto >= 0 ? GREEN : RED,
    },
    { label: "Vencido", value: fmtBRL(totalVencido), bg: [255, 243, 224], border: [255, 204, 128], color: AMBER },
  ]);

  if (sorted.length === 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text("Nenhum lançamento encontrado para os filtros aplicados.", PAGE_W / 2, y + 15, { align: "center" });
  } else {
    const body = sorted.map((item) => {
      const statusEff = item.status_display || item.status;
      const dias = statusEff === "vencido" ? diasVencido(item.data_vencimento) : 0;
      const isReceita = item.tipo === "receita";
      const entNome = isReceita ? item.cliente_nome : item.fornecedor_nome || item.cliente_nome;
      return [
        fmtDate(item.data_vencimento),
        isReceita ? "RECEBER" : "PAGAR",
        entNome || "—",
        item.descricao || "—",
        item.parcela || "—",
        `${isReceita ? "+" : "-"} ${fmtBRL(item.valor)}`,
        Number(item.juros || 0) > 0 ? fmtBRL(item.juros) : "—",
        Number(item.multa || 0) > 0 ? fmtBRL(item.multa) : "—",
        STATUS_LABELS_MAP[statusEff] || statusEff,
        item.data_pagamento ? fmtDate(item.data_pagamento) : "—",
        item.banco_nome || "—",
        dias > 0 ? `${dias}d` : "—",
      ];
    });

    autoTable(doc, {
      ...TABLE_STYLES,
      startY: y,
      head: [[
        "Vencimento", "Tipo", "Cliente / Fornecedor", "Descrição", "Parcela",
        "Valor", "Juros", "Multa", "Status", "Pgto/Receb.", "Banco", "Atraso",
      ]],
      body,
      foot: [[
        "TOTAL", "", `${receitas.length} a receber • ${despesas.length} a pagar`, "", "",
        `${saldoPrevisto >= 0 ? "+" : "-"} ${fmtBRL(Math.abs(saldoPrevisto))}`,
        "", "", "", "", "", "",
      ]],
      columnStyles: {
        0: { cellWidth: 18, halign: "center" },
        1: { cellWidth: 15, halign: "center", fontStyle: "bold", fontSize: 6.2 },
        2: { cellWidth: 40 },
        3: { cellWidth: 52 },
        4: { cellWidth: 13, halign: "center" },
        5: { cellWidth: 24, halign: "right", fontStyle: "bold" },
        6: { cellWidth: 15, halign: "right" },
        7: { cellWidth: 15, halign: "right" },
        8: { cellWidth: 16, halign: "center", fontStyle: "bold" },
        9: { cellWidth: 19, halign: "center" },
        10: { cellWidth: 27 },
        11: { cellWidth: 13, halign: "center" },
      },
      didParseCell: (data) => {
        if (data.section !== "body") return;
        if (data.column.index === 1) {
          data.cell.styles.textColor = data.cell.raw === "RECEBER" ? GREEN : RED;
        }
        if (data.column.index === 5) {
          data.cell.styles.textColor = String(data.cell.raw).startsWith("+") ? GREEN : RED;
        }
        if (data.column.index === 8) {
          const st = Object.keys(STATUS_LABELS_MAP).find((k) => STATUS_LABELS_MAP[k] === data.cell.raw);
          if (st && STATUS_COLORS[st]) data.cell.styles.textColor = STATUS_COLORS[st];
        }
        if (data.column.index === 11 && data.cell.raw !== "—") {
          data.cell.styles.textColor = RED;
          data.cell.styles.fontStyle = "bold";
        }
      },
    });
  }

  drawFooters(
    doc,
    `${sorted.length} ${sorted.length === 1 ? "registro" : "registros"} (${receitas.length} a receber • ${despesas.length} a pagar)`,
    dataEmissao,
  );

  const filename = `Relatorio_Consolidado_Pagar-Receber_${safeName(filters.dataInicio)}_a_${safeName(filters.dataFim)}.pdf`;
  return { blob: doc.output("blob"), filename };
}
